import { isAfter, startOfDay } from 'date-fns'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { CollapsibleIcon } from '@wartek-id/collapsible'
import {
  CollapsibleContent,
  CollapsibleTrigger,
} from '@wartek-id/collapsible-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast-v2'

import { InvalidUserNoAccessPelaksanaan } from 'api/kinerja/kepala-sekolah/config'
import {
  API_PATH_PREDIKAT_KINERJA_STEP,
  useAPIGetPelaksanaanConfig,
  useAPIGetPenetapanPredikatKinerjaStep,
  useAPIGetPredikatKinerjaList,
  useAPIUpsertPredikatKinerjaList,
} from 'api/kinerja/useAPIKinerja'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { SectionPanduanPenetapanPredikatKinerja } from 'app/KinerjaPage/pages/PelaksanaanV2/components/PenetapanPredikatKinerja/SectionPanduanPenetapanPredikatKinerja'
import { TablePenilaianKinerja } from 'app/KinerjaPage/pages/PelaksanaanV2/components/TablePenilaianKinerja'
import {
  ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT,
  PREDICATE_RECOMMENDATION_RESULT_TEXT,
} from 'app/KinerjaPage/pages/PelaksanaanV2/constants/predicateRecommendation'
import { CardCollapsible } from 'app/KinerjaShared/components/CardCollapsible'
import DialogSaveDraft from 'app/KinerjaShared/components/DialogSaveDraft'
import { PENGELOLAAN_KINERJA_KS_PENILAI_PATH } from 'app/KinerjaShared/constants/path'
import { isDateValid } from 'utils/datetime'
import { assetUrl } from 'utils/url'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'

import type { PredikatKinerjaItem } from 'types/kinerja-pelaksanaan'

const SUBMISSION_CONFIRMATION_TEXT =
  'SAYA MENGERTI BAHWA DATA TIDAK DAPAT DIUBAH LAGI'
const UNCOMPLETED_CONFIRMATION_TEXT =
  'SAYA SETUJU UNTUK MELAKUKAN PENILAIAN SUSULAN'
const MAX_DISPLAYED_UNCOMPLETED_ITEMS = 3

type PenetapanPredikatKinerjaProps = {
  npsn: string
}

export function PenetapanPredikatKinerja({
  npsn,
}: PenetapanPredikatKinerjaProps) {
  const isSubmitGuruEnabled =
    process.env.NEXT_PUBLIC_SKP_FORM_F_SUBMIT_GURU_ENABLED === 'true'

  const { trackEvent } = useSKPPelaksanaanTrackEvent()
  const router = useRouter()
  const toast = useToast()
  const { isDemoMode } = useDemoContext()
  const queryClient = useQueryClient()

  const [completedItems, setCompletedItems] = useState<
    Array<PredikatKinerjaItem>
  >([])
  const [isDirty, setIsDirty] = useState(false)
  const [activeDialog, setActiveDialog] = useState<
    'NONE' | 'SAVE_TO_DRAFT' | 'SUBMISSION' | 'GUIDE'
  >('NONE')

  const {
    data: dataPelaksanaanConfig,
    isSuccess: isSuccessPelaksanaanConfig,
    isFetching: isLoadingPelaksanaanConfig,
  } = useAPIGetPelaksanaanConfig({
    params: { key: 'PENETAPAN_PREDIKAT_KINERJA' },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const isPenetapanPredikatKinerjaStarted = useMemo(() => {
    // Deadline date
    const startDate =
      dataPelaksanaanConfig?.data?.predicatePerformanceAccessDate
    const isValidDate = isDateValid(startDate)
    const isStarted =
      isValidDate && isAfter(new Date(), startOfDay(new Date(startDate)))

    return isStarted
  }, [dataPelaksanaanConfig])

  const {
    data: dataPenetapanStep,
    isSuccess: isSuccessPenetapanStep,
    isLoading: isLoadingPenetapanStep,
  } = useAPIGetPenetapanPredikatKinerjaStep({
    params: {
      npsn,
    },
    options: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
      enabled: isPenetapanPredikatKinerjaStarted,
    },
    isDemoMode,
  })

  // Submission step-related values
  const isCorrectStep =
    dataPenetapanStep?.data?.submissionStep === 'SUBMIT_GURU'
  const isAllowed = isCorrectStep && isPenetapanPredikatKinerjaStarted

  const { isFetching: isLoadingGetList, data: dataGetList } =
    useAPIGetPredikatKinerjaList({
      params: { npsn },
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
        enabled: isAllowed && isSuccessPenetapanStep,
      },
      isDemoMode,
    })

  const upsertPredikatKinerjaMutation = useAPIUpsertPredikatKinerjaList(
    npsn,
    {},
    isDemoMode
  )

  // List-related values
  const uncompletedItems = dataGetList?.data?.notCompletedUser ?? []
  const hasUncompletedItems = uncompletedItems.length > 0
  const isLoadingAllData =
    isLoadingPelaksanaanConfig || isLoadingPenetapanStep || isLoadingGetList

  // Summary-related values
  const requiredPredicate =
    dataGetList?.data?.predicateOrganization?.requiredPredicate
  const requiredCount = dataGetList?.data?.predicateOrganization?.required ?? 0
  const organizationPredicate = dataGetList?.data?.predicateOrganization?.value
  const total = dataGetList?.data?.predicateOrganization?.total ?? 0
  const conformedCount: number =
    completedItems?.reduce((n, item) => {
      if (item.predicateRecommendation === requiredPredicate) {
        n++
      }
      return n
    }, 0) ?? 0
  const deficit = requiredCount - conformedCount
  const isDeficit = deficit > 0
  const isPredikatOrganisasiUpdated =
    dataGetList?.data?.predicateOrganization?.deliveryStatus === 'READY_TO_SEND'
  const isSubmitDisabled =
    upsertPredikatKinerjaMutation.isLoading || isLoadingAllData

  // Visually disable the button while keeping it clickable to show toast messages
  const isSubmitPartialDisabled =
    isSubmitDisabled || isPredikatOrganisasiUpdated || !isSubmitGuruEnabled

  const handleSaveToDraft = (shouldBackUponError = false) => {
    upsertPredikatKinerjaMutation.mutate(
      {
        submissionStep: 'SUBMIT_GURU',
        state: 'DRAFT',
        data: completedItems.map((item) => ({
          ptkId: item.ptkId,
          workBehavior: item.workBehavior?.result,
          performancePractice: item.performancePractice?.result,
        })),
      },
      {
        onSuccess: () => {
          if (isDemoMode) {
            queryClient.invalidateQueries(
              'demo-getPenetapanPredikatKinerjaStep'
            )
          }

          queryClient.invalidateQueries(
            `${API_PATH_PREDIKAT_KINERJA_STEP}/${npsn}`
          )
          setIsDirty(false)
          toast({
            message: 'Berhasil simpan draf!',
            type: 'success',
          })

          router.replace(
            `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/langkah-penetapan-predikat-kinerja`
          )
        },
        onError: () => {
          toast({
            message: 'Gagal simpan draf. Coba lagi.',
          })

          if (shouldBackUponError) {
            router.replace(
              `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/langkah-penetapan-predikat-kinerja`
            )
          }
        },
      }
    )
  }

  const handleSubmission = () => {
    if (!isSubmitDisabled && !isSubmitPartialDisabled) {
      upsertPredikatKinerjaMutation.mutate(
        {
          submissionStep: 'SUBMIT_GURU',
          state: 'SUBMITTED',
          data: completedItems.map((item) => ({
            ptkId: item.ptkId,
            workBehavior: item.workBehavior?.result,
            performancePractice: item.performancePractice?.result,
          })),
        },
        {
          onSuccess: () => {
            if (isDemoMode) {
              queryClient.invalidateQueries(
                'demo-getPenetapanPredikatKinerjaStep'
              )
            }

            queryClient.invalidateQueries(
              `${API_PATH_PREDIKAT_KINERJA_STEP}/${npsn}`
            )
            toast({
              message: 'Berhasil kirim ke guru!',
              type: 'success',
            })

            router.replace(
              `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/langkah-penetapan-predikat-kinerja`
            )
          },
          onError: (err: any) => {
            setActiveDialog('NONE')

            const hasUncompletedPerencanaan =
              err?.originalError?.response?.data?.error?.message ===
              'GURU_NOT_COMPLETED'

            toast({
              message: hasUncompletedPerencanaan
                ? 'Maaf, saat ini ada kendala di pengaliran data. Mohon coba lagi nanti, ya'
                : 'Gagal kirim ke guru. Coba lagi.',
            })
          },
        }
      )
    }
  }

  // Sync remote data with local state
  useEffect(() => {
    setCompletedItems(
      dataGetList?.data?.completedUser?.filter(
        (item) => item.ableSubmit === true
      ) ?? []
    )
  }, [dataGetList])

  // Redirect to hub if the step is incorrect
  useEffect(() => {
    if (
      isSuccessPenetapanStep &&
      isSuccessPelaksanaanConfig &&
      !isAllowed &&
      !upsertPredikatKinerjaMutation.isSuccess // Make sure this doesnt run upon cache invalidation
    ) {
      router.replace(
        `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${npsn}/langkah-penetapan-predikat-kinerja`
      )
      toast({
        message: 'Lakukan sesuai urutan, ya.',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAllowed,
    isSuccessPenetapanStep,
    upsertPredikatKinerjaMutation.isSuccess,
  ])

  // Show no access error if the Penetapan date is not started
  useEffect(() => {
    if (!isPenetapanPredikatKinerjaStarted && isSuccessPelaksanaanConfig) {
      throw new InvalidUserNoAccessPelaksanaan('path', null)
    }
  }, [isPenetapanPredikatKinerjaStarted, isSuccessPelaksanaanConfig])

  return (
    <>
      <main className="px-8 pb-8 pt-12 bg-gray-10 min-h-full">
        <div className="max-w-7xl mx-auto space-y-5">
          <Header
            title="Penetapan Predikat Kinerja"
            className="mb-9"
            rightNode={
              <Button
                variant="ghost"
                className="space-x-1 !bg-white"
                size="lg"
                onClick={() => setActiveDialog('GUIDE')}
              >
                <Icon color="inherit">help</Icon>
                <span>Panduan Penetapan</span>
              </Button>
            }
            onBack={() => {
              if (isDirty) {
                setActiveDialog('SAVE_TO_DRAFT')
              } else {
                router.back()
              }
            }}
          />

          <section>
            <div className="rounded-t-lg border bg-white px-5 pt-6 pb-4 space-y-4">
              <div className="flex items-start space-x-6">
                <div className="w-full space-y-1">
                  <Text variant="heading-md" as="h2">
                    Daftar Penetapan Predikat Kinerja
                  </Text>
                  <Text color="subdued">
                    Predikat Kinerja otomatis terisi berdasarkan rating.&nbsp;
                    {isDirty
                      ? 'Status: Ada perubahan oleh Anda, mohon simpan draf.'
                      : 'Ubah sesuai kebutuhan. Status: Belum ada perubahan.'}
                  </Text>
                </div>

                <div className="space-x-3 flex flex-shrink-0 items-start">
                  <Button
                    color="white"
                    size="lg"
                    isLoading={upsertPredikatKinerjaMutation.isLoading}
                    disabled={
                      upsertPredikatKinerjaMutation.isLoading ||
                      isLoadingAllData
                    }
                    onClick={() => {
                      trackEvent({
                        activity_type: 'penilaian_ks_pk_click_simpan_draf',
                        screen_name: 'ks_penetapan_kinerja',
                        event_trigger: 'ks_penilaian_hub',
                      })

                      handleSaveToDraft()
                    }}
                  >
                    Simpan draf
                  </Button>
                  <Button
                    className="space-x-1"
                    size="lg"
                    disabled={isSubmitDisabled}
                    aria-disabled={isSubmitPartialDisabled}
                    onClick={() => {
                      if (isPredikatOrganisasiUpdated) {
                        toast({
                          message:
                            'Predikat Kinerja Organisasi sedang diubah oleh Dinas Pendidikan. Cek lagi halaman ini nanti, ya.',
                        })
                      } else if (!isSubmitGuruEnabled) {
                        toast({
                          message:
                            'Mohon maaf, belum dapat lanjut karena halaman baru tersedia di bulan Juni.',
                        })
                      } else {
                        trackEvent({
                          activity_type: 'penilaian_ks_pk_click_kirim_ke_guru',
                          screen_name: 'ks_penetapan_kinerja',
                          event_trigger: 'ks_penilaian_hub',
                        })

                        setActiveDialog('SUBMISSION')
                      }
                    }}
                  >
                    <Icon color="inherit">lock</Icon>
                    <span>Kirim ke guru</span>
                  </Button>
                </div>
              </div>

              {isLoadingAllData ? (
                <Shimmer className="max-w-full !w-96 !h-24" />
              ) : isPredikatOrganisasiUpdated ? (
                <div className="py-2 px-3 border border-subdued rounded inline-block w-64 space-y-1">
                  <Text
                    variant="subheading"
                    color="subdued"
                    className="uppercase"
                  >
                    Predikat Kinerja Organisasi:
                  </Text>
                  <Text variant="helper" color="critical">
                    Sedang diubah oleh Dinas Pendidikan. Mohon ditunggu.
                  </Text>
                </div>
              ) : (
                <div className="flex items-stretch space-x-3">
                  <div className="py-2 px-3 border border-gray-30 rounded">
                    <p className="mb-5">
                      <Text
                        as="span"
                        variant="subheading"
                        color="subdued"
                        className="uppercase"
                      >
                        Predikat Kinerja Organisasi:
                      </Text>
                      <br></br>
                      <Text
                        as="span"
                        className="px-2 bg-blue-10 rounded inline-block !text-blue-60"
                      >
                        {ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT[
                          organizationPredicate
                        ] ?? '-'}
                      </Text>
                    </p>

                    <p className="flex items-center space-x-2">
                      <Text
                        as="span"
                        variant="subheading"
                        color="subdued"
                        className="uppercase"
                      >
                        Jumlah guru selesai penilaian:
                      </Text>
                      <Text
                        as="span"
                        className="px-2 bg-blue-10 rounded inline-block !text-blue-60"
                      >
                        {total}
                      </Text>
                    </p>
                  </div>
                  <div className="flex items-center">
                    <Icon>arrow_forward</Icon>
                  </div>
                  <div className="flex flex-col justify-center p-4 border border-gray-30 rounded space-y-1">
                    <Text
                      variant="subheading"
                      color="subdued"
                      className="uppercase"
                    >
                      Predikat Kinerja Pegawai:
                    </Text>
                    <div className="flex space-x-3 items-center">
                      <Text color="subdued" className="w-40">
                        {PREDICATE_RECOMMENDATION_RESULT_TEXT[
                          requiredPredicate
                        ] ?? '-'}
                      </Text>

                      <Text className="!text-blue-60 pr-6">
                        Min. {requiredCount} guru
                      </Text>

                      {isDeficit ? (
                        <Text
                          variant="body-sm"
                          className="!text-yellow-700 rounded bg-orange-10 py-1 px-2 h-auto"
                          color="warning"
                        >
                          Anjuran: Pilih {deficit} guru lagi
                        </Text>
                      ) : (
                        <p className="flex items-center rounded border border-green-40 bg-green-5 px-2 py-1 space-x-1 h-auto">
                          <Icon className="!text-base !text-green-60 !w-4 !h-4 !leading-none !cursor-default">
                            check
                          </Icon>
                          <Text
                            as="span"
                            className="!text-green-60"
                            variant="body-sm"
                          >
                            Sesuai anjuran
                          </Text>
                        </p>
                      )}
                    </div>
                    <Text variant="body" className="!text-blue-60">
                      Catatan: panduan ini bersifat anjuran.
                    </Text>
                  </div>
                </div>
              )}
            </div>

            <TablePenilaianKinerja
              isLoading={isLoadingAllData}
              items={completedItems}
              onChange={(items) => {
                setCompletedItems(items)
                setIsDirty(true)
              }}
            />
          </section>

          {hasUncompletedItems && (
            <section>
              <CardCollapsible>
                <CollapsibleTrigger type="button">
                  <div className="space-x-3 flex completedItems-center">
                    <Icon>info_outlined</Icon>
                    <Text variant="heading-sm" as="h2">
                      {uncompletedItems.length} penilaian belum selesai
                    </Text>
                  </div>
                  <CollapsibleIcon />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4">
                  <ul className="list-disc list-inside pl-10">
                    {uncompletedItems.map((item) => (
                      <Text as="li" color="subdued" key={item.ptkId}>
                        {item.name}
                      </Text>
                    ))}
                  </ul>

                  <div className="pl-10 mt-6">
                    <Text variant="body-bold" color="subdued" as="h3">
                      Perlu diketahui bahwa:
                    </Text>

                    <ol className="list-decimal list-outside pl-5">
                      <Text as="li" color="subdued">
                        Anda dapat tetap lanjut ke tahap berikutnya untuk guru
                        yang sudah selesai dinilai.
                      </Text>
                      <Text as="li" color="subdued">
                        Jika ada guru yang belum selesai dinilai, Anda dapat
                        melakukan penilaian dan penetapannya di tahap
                        <strong> Penilaian Susulan.</strong>
                      </Text>
                      <Text as="li" color="subdued">
                        Jika ada guru yang sudah tidak aktif (pensiun, meninggal
                        dunia, mutasi, dsb.) atau nama yang terduplikasi, mohon
                        kunjungi&nbsp;
                        <a
                          className="!text-blue-70 underline"
                          href={PANDUAN_PENGELOLAAN_URL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Pusat Bantuan.
                        </a>
                      </Text>
                    </ol>
                  </div>
                </CollapsibleContent>
              </CardCollapsible>
            </section>
          )}
        </div>
      </main>

      <SubmitConfirmationDialog
        uncompletedNames={uncompletedItems.map(({ name }) => name)}
        isOpen={activeDialog === 'SUBMISSION'}
        isSubmitting={upsertPredikatKinerjaMutation.isLoading}
        onClose={() => {
          setActiveDialog('NONE')
        }}
        onSubmit={handleSubmission}
      />

      <DialogSaveDraft
        data-testid="dialog-save-draft"
        isOpen={activeDialog === 'SAVE_TO_DRAFT'}
        onClickConfirm={() => handleSaveToDraft(true)}
        onClickCancel={() => setActiveDialog('NONE')}
        onClose={() => setActiveDialog('NONE')}
      />

      <Dialog
        isOpen={activeDialog === 'GUIDE'}
        size="lg"
        onClose={() => setActiveDialog('NONE')}
      >
        <DialogOverlay />
        <DialogContent>
          <SectionPanduanPenetapanPredikatKinerja
            actionNode={
              <Button type="button" onClick={() => setActiveDialog('NONE')}>
                Lanjut ke Penetapan
              </Button>
            }
            npsn={npsn}
            className="bg-white rounded-lg p-6"
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

type SubmitConfirmationDialogProps = {
  isOpen: boolean
  isSubmitting: boolean
  uncompletedNames: Array<string>
  onClose: () => void
  onSubmit: () => void
}

function SubmitConfirmationDialog({
  isOpen,
  isSubmitting,
  uncompletedNames,
  onClose,
  onSubmit,
}: SubmitConfirmationDialogProps) {
  const hasUncompletedNames = uncompletedNames.length > 0
  const [activeStep, setActiveStep] = useState<'UNCOMPLETED' | 'SUBMISSION'>(
    hasUncompletedNames ? 'UNCOMPLETED' : 'SUBMISSION'
  )

  useEffect(() => {
    if (isOpen) {
      setActiveStep(hasUncompletedNames ? 'UNCOMPLETED' : 'SUBMISSION')
    }
  }, [hasUncompletedNames, isOpen])

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      size="sm"
      onClose={onClose}
    >
      <DialogOverlay />
      <DialogContent>
        <div className="p-6 space-y-4">
          {activeStep === 'SUBMISSION' && (
            <StepSubmission
              isLoading={isSubmitting}
              onCancel={() => {
                if (hasUncompletedNames) {
                  setActiveStep('UNCOMPLETED')
                } else {
                  onClose()
                }
              }}
              onConfirm={onSubmit}
            />
          )}

          {activeStep === 'UNCOMPLETED' && (
            <StepUncompleted
              uncompletedNames={uncompletedNames}
              onCancel={onClose}
              onConfirm={() => setActiveStep('SUBMISSION')}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

type StepSubmissionProps = {
  isLoading: boolean
  onCancel?: () => void
  onConfirm?: () => void
}
function StepSubmission({
  isLoading,
  onCancel,
  onConfirm,
}: StepSubmissionProps) {
  const { trackEvent } = useSKPPelaksanaanTrackEvent()

  const [confirmation, setConfirmation] = useState('')

  const isConfirmed = confirmation === SUBMISSION_CONFIRMATION_TEXT

  // Reset confirmation state upon unmounting
  useEffect(() => {
    return () => {
      setConfirmation('')
    }
  }, [])

  return (
    <>
      <img
        src={assetUrl('/illustrations/img-publish-data.png')}
        alt=""
        width={84}
        height={84}
      />

      <Text variant="display-sm" as="h2">
        Setelah kirim, data tidak dapat diubah lagi dan guru akan menerima
        Dokumen Evaluasi Kinerja
      </Text>

      <div className="bg-blue-5 text-blue-60 p-1 rounded select-none">
        <Text variant="heading-sm" color="inherit">
          {SUBMISSION_CONFIRMATION_TEXT}
        </Text>
      </div>
      <Textarea
        className="max-w-xs"
        id="confirmation-text-input"
        placeholder={`Ketik "${SUBMISSION_CONFIRMATION_TEXT}" di sini`}
        autoComplete="off"
        rows={3}
        onChange={(e) => setConfirmation(e.target.value)}
      />

      <div className="flex space-x-3 !justify-start">
        <Button
          color="white"
          size="lg"
          onClick={() => {
            onCancel?.()

            trackEvent({
              activity_type: 'penilaian_ks_pk_click_popup_kembali',
              screen_name: 'ks_penetapan_kinerja',
              event_trigger: 'ks_penilaian_hub',
            })
          }}
        >
          Kembali
        </Button>
        <Button
          color="blue"
          disabled={!isConfirmed || isLoading}
          size="lg"
          type="button"
          isLoading={isLoading}
          onClick={() => {
            onConfirm?.()

            trackEvent({
              activity_type: 'penilaian_ks_pk_click_popup_kirim',
              screen_name: 'ks_penetapan_kinerja',
              event_trigger: 'ks_penilaian_hub',
            })
          }}
        >
          Kirim
        </Button>
      </div>
    </>
  )
}

type StepUncompletedProps = {
  uncompletedNames: Array<string>
  onCancel?: () => void
  onConfirm?: () => void
}
function StepUncompleted({
  uncompletedNames,
  onCancel,
  onConfirm,
}: StepUncompletedProps) {
  const [confirmation, setConfirmation] = useState('')

  const isConfirmed = confirmation === UNCOMPLETED_CONFIRMATION_TEXT

  const uncompletedNamesLength = uncompletedNames.length
  const remainingLength =
    uncompletedNamesLength - MAX_DISPLAYED_UNCOMPLETED_ITEMS

  // Reset confirmation state upon unmounting
  useEffect(() => {
    return () => {
      setConfirmation('')
    }
  }, [])

  return (
    <>
      <Icon
        className="!w-20 !h-20 !text-7xl flex items-center justify-center"
        color="critical"
      >
        rule
      </Icon>
      <Text variant="display-sm" as="h2">
        Lakukan penilaian dan penetapan untuk guru-guru berikut di tahap
        Penilaian Susulan:
      </Text>

      <ul className="list-disc pl-6 overflow-wrap-anywhere">
        {uncompletedNames
          .slice(0, MAX_DISPLAYED_UNCOMPLETED_ITEMS - 1)
          .map((name, i) => (
            <Text as="li" variant="body-lg" color="subdued" key={i}>
              {name}
            </Text>
          ))}
        {remainingLength === 0 && (
          <Text as="li" variant="body-lg" color="subdued">
            {uncompletedNames[MAX_DISPLAYED_UNCOMPLETED_ITEMS - 1]}
          </Text>
        )}
        {remainingLength >= 1 && (
          <Text as="li" variant="body-lg" color="subdued">
            {remainingLength + 1} orang lainnya
          </Text>
        )}
      </ul>

      <div className="bg-blue-5 text-blue-60 p-1 rounded select-none">
        <Text variant="heading-sm" color="inherit">
          {UNCOMPLETED_CONFIRMATION_TEXT}
        </Text>
      </div>
      <Textarea
        id="confirmation-text-input"
        placeholder={`Ketik "${UNCOMPLETED_CONFIRMATION_TEXT}" di sini`}
        autoComplete="off"
        rows={2}
        onChange={(e) => setConfirmation(e.target.value)}
      />

      <div className="flex space-x-3 !justify-start">
        <Button color="white" size="lg" onClick={onCancel}>
          Kembali
        </Button>
        <Button
          color="blue"
          disabled={!isConfirmed}
          size="lg"
          type="button"
          onClick={onConfirm}
        >
          Lanjut
        </Button>
      </div>
    </>
  )
}
